import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import {
  ApiEnvelope, Paged, Prospect, LeadStats, Call, NeedAnalysis, PlanCard, Quote,
  KycRecord, Proposal, Payment, Policy, Agent, AgentStats, AuditRow,
  PersistencyKpis, WorklistRow, Distribution, DriverRow, PolicyDetail,
  ChatMessage, Conversation, VoiceTurn, AiCallStart, AgentCallLog, CohortsResult,
  VoicesCatalog,
} from './models';

type Q = Record<string, string | number | boolean | undefined | null>;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private base = environment.apiBase;

  // Auth header for raw fetch() calls (SSE) — HttpClient requests get the token
  // from the auth interceptor, but fetch() bypasses interceptors.
  private authHeaders(): Record<string, string> {
    const t = this.auth.token();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }

  private params(q?: Q): HttpParams {
    let p = new HttpParams();
    if (q) for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p = p.set(k, String(v));
    return p;
  }
  private data<T>(o: Observable<ApiEnvelope<T>>): Observable<T> { return o.pipe(map((r) => r.data)); }
  private paged<T>(o: Observable<ApiEnvelope<T[]>>): Observable<Paged<T>> {
    return o.pipe(map((r) => ({ items: r.data, meta: r.meta! })));
  }

  // ── Leads & calls ──
  leads(q?: Q): Observable<Paged<Prospect>> {
    return this.paged(this.http.get<ApiEnvelope<Prospect[]>>(`${this.base}/leads`, { params: this.params(q) }));
  }
  leadStats(): Observable<LeadStats> { return this.data(this.http.get<ApiEnvelope<LeadStats>>(`${this.base}/leads/stats`)); }
  lead(id: string): Observable<Prospect> { return this.data(this.http.get<ApiEnvelope<Prospect>>(`${this.base}/leads/${id}`)); }
  scheduleCall(body: { prospectId: string; scheduledFor?: string }): Observable<Call> {
    return this.data(this.http.post<ApiEnvelope<Call>>(`${this.base}/calls/schedule`, body));
  }
  call(id: string): Observable<Call> { return this.data(this.http.get<ApiEnvelope<Call>>(`${this.base}/calls/${id}`)); }
  startCall(id: string, body: { stepMs?: number } = {}): Observable<any> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/calls/${id}/start`, body));
  }
  disclosureAck(id: string): Observable<any> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/calls/${id}/disclosure-ack`, {}));
  }
  endCall(id: string): Observable<Call> { return this.data(this.http.post<ApiEnvelope<Call>>(`${this.base}/calls/${id}/end`, {})); }

  // ── Need analysis & plans ──
  createNeedAnalysis(body: { prospectId: string; callId?: string }): Observable<NeedAnalysis> {
    return this.data(this.http.post<ApiEnvelope<NeedAnalysis>>(`${this.base}/need-analysis`, body));
  }
  needAnalysis(id: string): Observable<NeedAnalysis> { return this.data(this.http.get<ApiEnvelope<NeedAnalysis>>(`${this.base}/need-analysis/${id}`)); }
  recommendPlans(q: { needId?: string; prospectId?: string }): Observable<{ recommended: any; plans: PlanCard[] }> {
    return this.data(this.http.get<ApiEnvelope<any>>(`${this.base}/plans/recommend`, { params: this.params(q) }));
  }
  selectPlan(body: { prospectId: string; needId?: string; uin: string }): Observable<any> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/plans/select`, body));
  }

  // ── Quotation ──
  createQuote(body: { prospectId: string; needId?: string; uin: string }): Observable<Quote> {
    return this.data(this.http.post<ApiEnvelope<Quote>>(`${this.base}/quotes`, body));
  }
  dispatchQuote(id: string, channels?: string[]): Observable<any> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/quotes/${id}/dispatch`, { channels }));
  }
  quote(id: string): Observable<Quote> { return this.data(this.http.get<ApiEnvelope<Quote>>(`${this.base}/quotes/${id}`)); }
  quoteAudit(id: string): Observable<AuditRow[]> { return this.data(this.http.get<ApiEnvelope<AuditRow[]>>(`${this.base}/quotes/${id}/audit`)); }

  // ── KYC ──
  kycConsent(prospectId: string): Observable<KycRecord> { return this.data(this.http.post<ApiEnvelope<KycRecord>>(`${this.base}/kyc/consent`, { prospectId })); }
  kycAadhaarInitiate(prospectId: string): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/kyc/aadhaar/initiate`, { prospectId })); }
  kycAadhaarVerify(prospectId: string, txnId: string): Observable<KycRecord> { return this.data(this.http.post<ApiEnvelope<KycRecord>>(`${this.base}/kyc/aadhaar/verify-otp`, { prospectId, txnId })); }
  kycPan(prospectId: string): Observable<KycRecord> { return this.data(this.http.post<ApiEnvelope<KycRecord>>(`${this.base}/kyc/pan`, { prospectId })); }
  kycDigilocker(prospectId: string): Observable<KycRecord> { return this.data(this.http.get<ApiEnvelope<KycRecord>>(`${this.base}/kyc/digilocker/callback`, { params: this.params({ prospectId }) })); }
  kycAml(prospectId: string): Observable<KycRecord> { return this.data(this.http.post<ApiEnvelope<KycRecord>>(`${this.base}/kyc/aml-screen`, { prospectId })); }
  kyc(id: string): Observable<KycRecord> { return this.data(this.http.get<ApiEnvelope<KycRecord>>(`${this.base}/kyc/${id}`)); }

  // ── Proposal & e-sign ──
  createProposal(body: { prospectId: string; quoteId: string; nominee?: any }): Observable<Proposal> {
    return this.data(this.http.post<ApiEnvelope<Proposal>>(`${this.base}/proposal`, body));
  }
  esignInitiate(proposalId: string): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/esign/initiate`, { proposalId })); }
  esignCallback(proposalId: string): Observable<Proposal> { return this.data(this.http.post<ApiEnvelope<Proposal>>(`${this.base}/esign/callback`, { proposalId })); }
  proposal(id: string): Observable<Proposal> { return this.data(this.http.get<ApiEnvelope<Proposal>>(`${this.base}/proposal/${id}`)); }

  // ── Payments ──
  createPaymentLink(proposalId: string): Observable<Payment> { return this.data(this.http.post<ApiEnvelope<Payment>>(`${this.base}/payments/link`, { proposalId })); }
  paymentWebhook(paymentId: string, method = 'UPI'): Observable<Payment> { return this.data(this.http.post<ApiEnvelope<Payment>>(`${this.base}/payments/webhook`, { paymentId, method })); }
  paymentStatus(id: string): Observable<Payment> { return this.data(this.http.get<ApiEnvelope<Payment>>(`${this.base}/payments/${id}/status`)); }
  enach(paymentId: string): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/mandates/enach`, { paymentId })); }

  // ── COI ──
  issueCoi(proposalId: string): Observable<Policy> { return this.data(this.http.post<ApiEnvelope<Policy>>(`${this.base}/coi/issue`, { proposalId })); }
  deliverCoi(policyNumber: string, channels?: string[]): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/coi/${policyNumber}/deliver`, { channels })); }
  coi(id: string): Observable<Policy> { return this.data(this.http.get<ApiEnvelope<Policy>>(`${this.base}/coi/${id}`)); }

  // ── Agent Studio ──
  agents(q?: Q): Observable<Paged<Agent>> { return this.paged(this.http.get<ApiEnvelope<Agent[]>>(`${this.base}/agents`, { params: this.params(q) })); }
  agentStats(): Observable<AgentStats> { return this.data(this.http.get<ApiEnvelope<AgentStats>>(`${this.base}/agents/stats`)); }
  // Sarvam TTS voice catalog + the languages Sarvam can voice (for Persona & voice).
  voices(): Observable<VoicesCatalog> { return this.data(this.http.get<ApiEnvelope<VoicesCatalog>>(`${this.base}/agents/voices`)); }
  agent(id: string): Observable<Agent> { return this.data(this.http.get<ApiEnvelope<Agent>>(`${this.base}/agents/${id}`)); }
  createAgent(body: Partial<Agent>): Observable<Agent> { return this.data(this.http.post<ApiEnvelope<Agent>>(`${this.base}/agents`, body)); }
  updateAgent(id: string, body: Partial<Agent>): Observable<Agent> { return this.data(this.http.put<ApiEnvelope<Agent>>(`${this.base}/agents/${id}`, body)); }
  deleteAgent(id: string): Observable<any> { return this.data(this.http.delete<ApiEnvelope<any>>(`${this.base}/agents/${id}`)); }
  // PDF/SHEET/FAQ/QA sources send the selected file as multipart/form-data; URL sources send the url.
  // Do NOT set a content-type header — HttpClient adds the multipart boundary for FormData automatically.
  addKb(id: string, body: { type: string; name?: string; url?: string; file?: File | null }): Observable<Agent> {
    const form = new FormData();
    form.append('type', body.type);
    if (body.name) form.append('name', body.name);
    if (body.url) form.append('url', body.url);
    if (body.file) form.append('file', body.file, body.file.name);
    return this.data(this.http.post<ApiEnvelope<Agent>>(`${this.base}/agents/${id}/kb`, form));
  }
  removeKb(id: string, srcId: string): Observable<Agent> { return this.data(this.http.delete<ApiEnvelope<Agent>>(`${this.base}/agents/${id}/kb/${srcId}`)); }
  startTestCall(id: string, body: { phone?: string; stepMs?: number }): Observable<{ callId: string; status: string; provider: string; simulated: boolean; to: string | null }> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/agents/${id}/test-call`, body));
  }
  endTestCall(id: string, callId: string): Observable<any> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/agents/${id}/test-call/${callId}/end`, {}));
  }
  testAgent(id: string, messages: ChatMessage[]): Observable<{ reply: string; simulated: boolean; provider: string }> {
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/agents/${id}/test`, { messages }));
  }
  /**
   * Streaming chat: hits the SSE endpoint and invokes `onDelta` with each token
   * chunk as it arrives (so the UI shows the reply forming), resolving with the
   * final reply + metadata. Uses fetch directly since Angular's HttpClient
   * buffers the whole response.
   */
  async testAgentStream(
    id: string,
    messages: ChatMessage[],
    onDelta: (text: string) => void,
  ): Promise<{ reply: string; simulated: boolean; grounded: boolean; provider: string }> {
    const res = await fetch(`${this.base}/agents/${id}/test-stream`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify({ messages }),
    });
    if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let final = { reply: '', simulated: false, grounded: false, provider: '' };
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const blocks = buf.split('\n\n'); // SSE events are separated by a blank line
      buf = blocks.pop() || '';
      for (const block of blocks) {
        const ev = this.parseSse(block);
        if (!ev) continue;
        if (ev.event === 'delta' && ev.data?.text) onDelta(ev.data.text);
        else if (ev.event === 'done') final = ev.data;
      }
    }
    return final;
  }

  private parseSse(block: string): { event: string; data: any } | null {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) return null;
    try { return { event, data: JSON.parse(dataLines.join('\n')) }; } catch { return null; }
  }
  // Voice turn: send mic audio (or text) → Sarvam STT → KB → OpenAI → Sarvam TTS.
  voiceTurn(id: string, audio: Blob | null, history: ChatMessage[]): Observable<VoiceTurn> {
    const form = new FormData();
    if (audio) form.append('audio', audio, 'turn.webm');
    form.append('history', JSON.stringify(history));
    return this.data(this.http.post<ApiEnvelope<VoiceTurn>>(`${this.base}/agents/${id}/voice-turn`, form));
  }
  /**
   * Low-latency voice turn over SSE. Emits the transcript, then streams the reply
   * text and per-sentence TTS audio as each is ready, resolving with the final
   * metadata. The caller plays audio chunks in arrival order.
   */
  async voiceTurnStream(
    id: string,
    audio: Blob | null,
    history: ChatMessage[],
    handlers: { onUser?: (t: string) => void; onDelta?: (t: string) => void; onAudio?: (b64: string, mime: string | null) => void },
  ): Promise<{ userText: string; reply: string; grounded: boolean; sttSimulated: boolean; error?: string }> {
    const form = new FormData();
    if (audio) form.append('audio', audio, 'turn.webm');
    form.append('history', JSON.stringify(history));
    const res = await fetch(`${this.base}/agents/${id}/voice-turn-stream`, { method: 'POST', headers: { ...this.authHeaders() }, body: form });
    if (!res.ok || !res.body) throw new Error(`voice stream ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let final: { userText: string; reply: string; grounded: boolean; sttSimulated: boolean; error?: string } =
      { userText: '', reply: '', grounded: false, sttSimulated: false };
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const blocks = buf.split('\n\n');
      buf = blocks.pop() || '';
      for (const block of blocks) {
        const ev = this.parseSse(block);
        if (!ev) continue;
        if (ev.event === 'user') { final.userText = ev.data.userText; final.sttSimulated = ev.data.sttSimulated; handlers.onUser?.(ev.data.userText); }
        else if (ev.event === 'delta') handlers.onDelta?.(ev.data.text);
        else if (ev.event === 'audio') { if (ev.data.audio) handlers.onAudio?.(ev.data.audio, ev.data.mimeType); }
        else if (ev.event === 'done') final = { ...final, ...ev.data };
      }
    }
    return final;
  }
  // ── Journey AI calling (Exotel) ──
  startAiCall(body: { prospectId: string; agentId: string }): Observable<AiCallStart> {
    return this.data(this.http.post<ApiEnvelope<AiCallStart>>(`${this.base}/journey/ai-call`, body));
  }
  endAiCall(id: string): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/journey/ai-call/${id}/end`, {})); }
  aiCalls(q?: Q): Observable<AgentCallLog[]> { return this.data(this.http.get<ApiEnvelope<AgentCallLog[]>>(`${this.base}/journey/ai-calls`, { params: this.params(q) })); }
  aiCall(id: string): Observable<AgentCallLog> { return this.data(this.http.get<ApiEnvelope<AgentCallLog>>(`${this.base}/journey/ai-call/${id}`)); }
  saveConversation(id: string, body: { title?: string; messages: ChatMessage[] }): Observable<Conversation> {
    return this.data(this.http.post<ApiEnvelope<Conversation>>(`${this.base}/agents/${id}/conversations`, body));
  }
  conversations(id: string): Observable<Conversation[]> {
    return this.data(this.http.get<ApiEnvelope<Conversation[]>>(`${this.base}/agents/${id}/conversations`));
  }

  // ── Persistency ──
  persistencyBook(q?: Q): Observable<Paged<WorklistRow>> { return this.paged(this.http.get<ApiEnvelope<WorklistRow[]>>(`${this.base}/persistency/book`, { params: this.params(q) })); }
  persistencyKpis(): Observable<PersistencyKpis> { return this.data(this.http.get<ApiEnvelope<PersistencyKpis>>(`${this.base}/persistency/kpis`)); }
  persistencyDistribution(): Observable<Distribution> { return this.data(this.http.get<ApiEnvelope<Distribution>>(`${this.base}/persistency/distribution`)); }
  persistencyDrivers(): Observable<DriverRow[]> { return this.data(this.http.get<ApiEnvelope<DriverRow[]>>(`${this.base}/persistency/drivers`)); }
  persistencyCohorts(): Observable<CohortsResult> { return this.data(this.http.get<ApiEnvelope<CohortsResult>>(`${this.base}/persistency/cohorts`)); }
  persistencyDetail(id: string): Observable<PolicyDetail> { return this.data(this.http.get<ApiEnvelope<PolicyDetail>>(`${this.base}/persistency/${id}`)); }
  persistencyIntervene(policyId: string): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/persistency/intervene`, { policyId })); }
  persistencySimulate(): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/persistency/simulate`, {})); }
  persistencyReset(): Observable<any> { return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/persistency/reset`, {})); }
  // Bulk-import customers from a CSV (multipart). Don't set content-type — HttpClient adds the boundary.
  persistencyImport(file: File): Observable<{ imported: number; duplicates: number; skipped: number; total: number; errors: { line: number; errors: string[] }[] }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.data(this.http.post<ApiEnvelope<any>>(`${this.base}/persistency/import`, form));
  }
}
